import { useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import './Header.css';
import StayNavigation from './StayNavigation';

gsap.registerPlugin(useGSAP);
const defaultLinks = [
  { label: 'Home', href: '/' },
  { label: 'Stay', href: '/stay' },
  { label: 'Explore', href: '/explore' },
  { label: 'Our story', href: '/our-story' },
  { label: 'Contact', href: '/contact' },
];

function Header({ brand = 'JamRock Escape', homeHref = '#home', links = defaultLinks }) {
  const scope = useRef(null);
  const toggle = useRef(null);
  const menuTimeline = useRef(null);
  const [open, setOpen] = useState(false);

  useGSAP(() => {
    const media = gsap.matchMedia();
    media.add({ mobile: '(max-width: 899px)', reduce: '(prefers-reduced-motion: reduce)' }, ({ conditions }) => {
      if (!conditions.mobile) return;
      menuTimeline.current = gsap.timeline({ paused: true })
        .to('.site-header__mobile', {
          height: 'auto', autoAlpha: 1,
          duration: conditions.reduce ? 0.001 : 0.3, ease: 'power3.out',
        })
        .fromTo('.site-header__mobile a', { opacity: 0, y: conditions.reduce ? 0 : -8 }, {
          opacity: 1, y: 0, stagger: conditions.reduce ? 0 : 0.035,
          duration: conditions.reduce ? 0.001 : 0.25,
        }, 0);
      return () => { menuTimeline.current = null; };
    });
    return () => media.revert();
  }, { scope });

  useGSAP(() => {
    if (menuTimeline.current) {
      if (open) menuTimeline.current.play();
      else menuTimeline.current.reverse();
    }
  }, { scope, dependencies: [open] });

  useEffect(() => {
    const breakpoint = window.matchMedia('(min-width: 900px)');
    const closeOnDesktop = () => { if (breakpoint.matches) setOpen(false); };
    breakpoint.addEventListener('change', closeOnDesktop);
    return () => breakpoint.removeEventListener('change', closeOnDesktop);
  }, []);

  function closeOnEscape(event) {
    if (event.key === 'Escape' && open) {
      setOpen(false);
      toggle.current.focus();
    }
  }

  return (
    <header ref={scope} className="site-header" id="home" onKeyDown={closeOnEscape}>
      <a className="skip-link" href="#main-content">Skip to content</a>
      <div className="site-header__inner">
        <a className="site-header__brand" href={homeHref} aria-label={`${brand} home`}>
          <img src={`${process.env.PUBLIC_URL}/images/logo.jpg`} alt={brand} width="130" height="87" />
        </a>
        <nav className="site-header__nav" aria-label="Main navigation">
          {links.map(({ label, href }) => /^\/stays?\/?$/.test(href) ? <StayNavigation key={label} /> : <a key={label} href={href}>{label}</a>)}
        </nav>
        <a className="site-header__cta" href="/booking">Find your stay <span aria-hidden="true">↗</span></a>
        <button ref={toggle} className="site-header__toggle" type="button" aria-expanded={open} aria-controls="mobile-menu" aria-label={open ? 'Close menu' : 'Open menu'} onClick={() => setOpen(!open)}>
          <span /><span /><span />
        </button>
      </div>
      <nav id="mobile-menu" className="site-header__mobile" aria-label="Mobile navigation" aria-hidden={!open} inert={!open}>
        <div className="site-header__mobile-inner">
          {links.map(({ label, href }) => /^\/stays?\/?$/.test(href) ? <StayNavigation key={label} mobile menuOpen={open} onNavigate={() => setOpen(false)} /> : <a key={label} href={href} onClick={() => setOpen(false)}>{label}</a>)}
          <a className="site-header__mobile-cta" href="/booking" onClick={() => setOpen(false)}>Find your stay <span aria-hidden="true">↗</span></a>
        </div>
      </nav>
    </header>
  );
}
export default Header;
